import SideBar from "./components/SideBar";
import "./styles/master.css";
import "./styles/App.style.css";
import Monitor from "./components/Monitor";
import EquipmentExplorer from "./pages/EquipmentExplorer";
import Reprocess from "./pages/Reprocess";
import Login from "./pages/Login";
import React, { useEffect, useRef, useState } from "react";
import resourcesService from "./services/Resources";
import { io, Socket } from "socket.io-client";
import {
  EnvironmentsData,
  Resource,
  ResourcesData,
  AwsMetric,
} from "./models/ResourcesTypes";

const DEFAULT_TIMEFRAME_CALL = "6h";

function downSampleData(data: AwsMetric[], maxPoints = 25) {
  if (data.length <= maxPoints) return data;
  const step = Math.ceil(data.length / maxPoints);
  const downSampled = [];
  for (let i = 0; i < data.length; i += step) {
    const chunk = data.slice(i, i + step);
    const summedValue = chunk.reduce((acc, item) => acc + (item.Sum || 0), 0);
    downSampled.push({
      ...chunk[0],
      Sum: summedValue,
    });
  }
  return downSampled;
}

function isResourceInError(resource: Resource) {
  if (
    resource.subtype === "athena" ||
    !resource.data ||
    (resource.data as AwsMetric[]).length === 0
  ) {
    return false;
  }
  const downSampledData = downSampleData(resource.data as AwsMetric[]);
  return downSampledData[downSampledData.length - 1].Sum > 0;
}

function calculateHealth(
  resources: ResourcesData,
): "green" | "yellow" | "red" | null {
  if (!resources || Object.keys(resources).length === 0 || resources.error) {
    return null;
  }

  let totalResources = 0;
  let errorResources = 0;

  Object.keys(resources).forEach((env) => {
    if (env === "datetime" || env === "error") return;
    const envResources = resources[env] as Resource[];
    envResources.forEach((resource) => {
      if (resource.subtype === "athena") return;
      totalResources++;
      if (isResourceInError(resource)) {
        errorResources++;
      }
    });
  });

  if (totalResources === 0) return null;

  const successRate =
    ((totalResources - errorResources) / totalResources) * 100;
  if (successRate >= 90) return "green";
  if (successRate >= 70) return "yellow";
  return "red";
}

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    return localStorage.getItem("kpi_radar_auth") === "true";
  });
  const socketRef = useRef<Socket>({} as Socket);
  const [logs, setLogs] = useState([] as string[]);
  const [environments, setEnvironments] = useState<EnvironmentsData>({});
  const [resources, setResources] = useState<ResourcesData>({});
  const [filter, setFilter] = useState("");
  const [statusType, setStatusType] = useState("");
  const [resourceType, setResourceType] = useState("");
  const [anyFilter, setAnyFilter] = useState(false);
  const [zeroRun, setZeroRun] = useState(false);
  const [timeframe, setTimeframe] = useState(DEFAULT_TIMEFRAME_CALL);
  const [reload, setReload] = useState(false);
  const [healthStatus, setHealthStatus] = useState<
    "green" | "yellow" | "red" | null
  >(null);
  const [autoRefresh, setAutoRefresh] = useState<boolean>(() => {
    const saved = localStorage.getItem("autoRefresh");
    return saved ? JSON.parse(saved) : false;
  });
  const [autoRefreshInterval, setAutoRefreshInterval] = useState<number>(() => {
    const saved = localStorage.getItem("autoRefreshInterval");
    return saved ? parseInt(saved, 10) : 300000;
  });
  const [currentView, setCurrentView] = useState<
    "monitor" | "equipment" | "reprocess"
  >("monitor");
  const prevHealthStatus = useRef<"green" | "yellow" | "red" | null>(null);

  const handleLogout = () => {
    localStorage.removeItem("kpi_radar_auth");
    setIsAuthenticated(false);
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    socketRef.current = io({
      path: "/socket.io",
    });
    socketRef.current.on("logs", (msg: string) => {
      setLogs((prevLogs) => [...prevLogs, msg]);
    });
    return () => {
      if (socketRef.current && socketRef.current.disconnect) {
        socketRef.current.disconnect();
      }
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    let ignore = false;

    if (reload) {
      setReload(false);
    }
    setLogs([]);
    setEnvironments({});
    setResources({});
    resourcesService
      .getEnvironments()
      .then((envs) => {
        if (ignore) return;
        setEnvironments(envs);
        const resourcePromises = Object.keys(envs).map((env) =>
          resourcesService
            .getResources(timeframe, envs, env)
            .then((res: ResourcesData) => ({
              name: env,
              data: res[env],
              datetime: res.datetime,
            }))
            .catch(() => ({ name: env, error: true })),
        );
        return Promise.all(resourcePromises);
      })
      .then((envs) => {
        if (ignore) return;
        if (!envs) return;
        let hasError = false;
        const newResources: ResourcesData = {};
        envs.forEach((env: Record<string, string | boolean | Resource[]>) => {
          if (env.error) {
            hasError = true;
          } else {
            newResources[env.name as string] = env.data;
            newResources.datetime = env.datetime;
          }
        });
        if (hasError) {
          setResources({ error: true });
          setHealthStatus(null);
        } else {
          setResources(newResources);
          setHealthStatus(calculateHealth(newResources));
        }
      });

    return () => {
      ignore = true;
    };
  }, [timeframe, reload, isAuthenticated]);

  useEffect(() => {
    localStorage.setItem("autoRefresh", JSON.stringify(autoRefresh));
  }, [autoRefresh]);

  useEffect(() => {
    localStorage.setItem("autoRefreshInterval", autoRefreshInterval.toString());
  }, [autoRefreshInterval]);

  useEffect(() => {
    if (!isAuthenticated || !autoRefresh) return;

    const interval = setInterval(() => {
      setReload(true);
    }, autoRefreshInterval);

    return () => clearInterval(interval);
  }, [isAuthenticated, autoRefresh, autoRefreshInterval]);

  useEffect(() => {
    if (!healthStatus || healthStatus === prevHealthStatus.current) {
      prevHealthStatus.current = healthStatus;
      return;
    }

    prevHealthStatus.current = healthStatus;
  }, [healthStatus]);

  if (!isAuthenticated) {
    return <Login onLogin={() => setIsAuthenticated(true)} />;
  }

  return (
    <div className="app-container">
      <SideBar
        setStatusType={setStatusType}
        setFilter={setFilter}
        setResourceType={setResourceType}
        setAnyFilter={setAnyFilter}
        setZeroRun={setZeroRun}
        reload={reload}
        currentView={currentView}
        setCurrentView={setCurrentView}
      ></SideBar>
      {currentView === "equipment" ? (
        <EquipmentExplorer />
      ) : currentView === "reprocess" ? (
        <Reprocess environments={environments} logs={logs} />
      ) : (
        <Monitor
          environments={environments}
          resources={resources}
          statusType={statusType}
          filter={filter}
          setFilter={setFilter}
          timeframe={timeframe}
          setTimeframe={setTimeframe}
          setReload={setReload}
          resourceType={resourceType}
          anyFilter={anyFilter}
          zeroRun={zeroRun}
          logs={logs}
          healthStatus={healthStatus}
          autoRefresh={autoRefresh}
          setAutoRefresh={setAutoRefresh}
          autoRefreshInterval={autoRefreshInterval}
          setAutoRefreshInterval={setAutoRefreshInterval}
          onLogout={handleLogout}
        ></Monitor>
      )}
    </div>
  );
};

export default App;
